import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { hashKey } from '@tanstack/react-query'
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import { PboKoLocale } from '@/components/pbo/PboKoLocale'
import { PboConfirm } from '@/components/pbo/pboDialog'
import { failureError, invalidError, notice, rowInvalidError } from '@/components/pbo/notice'
import { withNewMarker, withoutNewMarker } from '@/components/pbo/newRowMarker'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import Loader from '@/components/content/Loader'
import { canSelectShipper, defaultShipper, scopedShipperCode, selectableShippers, shipperScopeOf, withCoMingle } from '@/components/system/shipperScope'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { useLeaveGuard } from '@/components/layout/leaveGuard'
import { FETCH_ALL_MESSAGES, keepEditedSince, noticeIfUploadExceedsPage } from '@/hooks/useFetchAllRows'
import {
  ERROR_OUTBOUND_SKU_UNMAPPED,
  ETC_OUTBOUND_AVAILABLE_SHIPPERS,
  ETC_OUTBOUND_DOWNLOAD_URL,
  ETC_OUTBOUND_FETCH_SIZE,
  ETC_OUTBOUND_MAX_CONFIRM_SIZE,
  ETC_OUTBOUND_MAX_SAVE_SIZE,
  ETC_OUTBOUND_SEARCH_TYPE,
  IF_REQ_OPTIONS,
  LOGISTICS_SERVICE_TYPE_OPTIONS,
  PROGRESS_COMPLETED,
  PROGRESS_FAILED,
  PROGRESS_MAX_ATTEMPTS,
  PROGRESS_MAX_FAILURES,
  TRANSPORT_TYPE_OPTIONS,
} from '@/services/outbound/outboundEtc/consts'
import {
  EtcOutboundDownloadParams,
  EtcOutboundDuplicateItem,
  EtcOutboundEditableRow,
  EtcOutboundSaveItem,
  EtcOutboundSavedItem,
  EtcOutboundSaveRes,
  EtcOutboundSearchParams,
} from '@/services/outbound/outboundEtc/types'
import {
  outboundEtcQueryKeys,
  useConfirmEtcOutbounds,
  useGetEtcOutboundShippers,
  useGetEtcOutbounds,
  useGetOutboundProgress,
  useSaveEtcOutbounds,
} from '@/services/outbound/outboundEtc/useOutboundEtcQueries'
import { DOWNLOAD_REASON_EMPTY_MESSAGE } from '@/utils/consts'
import { EXPORT_LOG_MENU_CD, MESSAGES, REASON_DETAIL_MIN_LENGTH } from '@/pages/outbound/outboundEtc/constants'
import { excludeAllOption, toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'
import { parseEtcOutboundExcel } from '@/pages/outbound/outboundEtc/excelIO'
import { callToAddImportLog } from '@/services/excel/apis'
import useExcelDownload from '@/hooks/useDownloadExcel'
import { DOWNLOAD_PASSWORD_INVALID_MESSAGE, isValidDownloadPassword } from '@/utils/downloadPassword'
import { notifyUnallocated } from '@/pages/outbound/outboundEtc/unallocatedNotice'
import { toConfirmBody, toEditableRows, toSaveBody, toSaveItem, toSearchBody } from '@/pages/outbound/outboundEtc/payload'
import { EtcOutboundSearchQuery, fromSearchQuery, toSearchQuery } from '@/pages/outbound/outboundEtc/searchQuery'
import {
  findInvalidPhoneRow,
  findInvalidTransportTypeRow,
  findInvalidZipCodeRow,
  findMissingRequired,
  subjectParticle,
} from '@/pages/outbound/outboundEtc/validation'
import { DuplicateOrderModal, SkuUnmappedModal } from '@/pages/outbound/outboundEtc/components'
import { OutboundProgressModal } from '@/pages/outbound/components/OutboundProgressModal'
import EtcOutboundPageView, { type ActionCtx, type Row, type Wiring } from './handoff/page.generated'
import { usePermissionSlot } from '@/hooks/useMenuPermission'
import { coreTableLayoutSlot } from '@/hooks/useCoreTableLayout'

/** 뷰가 onState 로 알리는 화면 상태(페이저·목록 검색어) */
type ViewState = Parameters<NonNullable<Wiring['onState']>>[0]

const EMPTY_PARAMS: EtcOutboundSearchParams = { ORD_NO: [], ORD_OPT_NO: [] }

/** 2.0 표준 응답(meta.message)과 1.0 그리드 응답(message) 어느 쪽으로 와도 서버 문구를 꺼낸다. */
const extractMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { meta?: { message?: string }; message?: string } } })?.response?.data
  return data?.meta?.message || data?.message || fallback
}

const extractSaveError = (error: unknown): EtcOutboundSaveRes | undefined =>
  (error as { response?: { data?: EtcOutboundSaveRes } })?.response?.data

/**
 * "이미 사용자에게 알린 실패" 표시 — 뷰 계약(컨덕터 #582)상 실패는 throw 해야 뷰가 선택·모달을 유지한다.
 * 슬롯 안에서 알럿·모달을 띄운 뒤 throw 하는데, 바깥 catch 가 같은 오류를 다시 알리지 않게 표시를 남긴다.
 */
const SHOWN = Symbol('alertShown')
const markShown = <T,>(e: T): T => {
  if (e && typeof e === 'object') (e as unknown as Record<symbol, boolean>)[SHOWN] = true
  return e
}
/** 거부 사유는 Error 로 — BE 응답 객체가 그대로 오면 감싼다(prefer-promise-reject-errors). 표시(SHOWN)는 원본과 래퍼 둘 다에 남긴다. */
const asError = (e: unknown): Error => {
  if (e instanceof Error) return e
  const wrapped = Object.assign(new Error(extractMessage(e, MESSAGES.requestFailed)), { cause: e })
  if (isAlreadyShown(e)) markShown(wrapped)
  return wrapped
}
const isAlreadyShown = (e: unknown): boolean => Boolean(e && typeof e === 'object' && (e as Record<symbol, boolean>)[SHOWN])

/** 다건 코드입력·멀티셀렉트 값 — 뷰는 배열 또는 undefined 로 준다. 조작된 값이 와도 문자열만 받고, '전체'(빈 문자열)는 거른다. */
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : [])
const text = (v: unknown): string => (typeof v === 'string' ? v : '')

/**
 * 뷰 조회영역 값 → 조회 조건(BE `EtcOutboundSearchParams`). 키는 생성본이 낸 것 그대로다 —
 * 화주 짝 필드(`etcOutShipper`)는 `"etcOutShipper.types"`(화주유형, 멀티) · `"etcOutShipper.names"`(화주명, 단일 선택이지만 값은 1칸 배열)로 오고,
 * 나머지는 filterKey(ORD_NO·ORD_OPT_NO·TRS_TYPE)다. '전체' 는 빈 문자열이라 걸러 `undefined` 로 떨어뜨린다(옛 필터 컴포넌트 dropAll).
 * BE 는 화주코드 단건(SPR_NM)만 받는다 — 화주명 칸도 단일 선택이다(LOGISPRD-14805, 1.0 과 같다).
 */
const toParams = (values: Record<string, unknown>): EtcOutboundSearchParams => {
  const types = asList(values['etcOutShipper.types'])
  const codes = asList(values['etcOutShipper.names'])
  return {
    SPR_NM: codes[0] || undefined,
    ORD_NO: asList(values.ORD_NO),
    ORD_OPT_NO: asList(values.ORD_OPT_NO),
    TRS_TYPE: text(values.TRS_TYPE) || undefined,
    LOGISTICS_SERVICE_TYPES: types.length ? types : undefined,
  }
}

/** 복원한 조회 조건 → 조회영역 값(생성본 키). 없는 값은 얹지 않는다(뷰 기본값 '전체' 가 살아 있게). */
const restoredFilterValues = (params: EtcOutboundSearchParams): Record<string, unknown> => ({
  ...(params.LOGISTICS_SERVICE_TYPES?.length ? { 'etcOutShipper.types': params.LOGISTICS_SERVICE_TYPES } : {}),
  ...(params.SPR_NM ? { 'etcOutShipper.names': [params.SPR_NM] } : {}),
  ...(params.ORD_NO.length ? { ORD_NO: params.ORD_NO } : {}),
  ...(params.ORD_OPT_NO.length ? { ORD_OPT_NO: params.ORD_OPT_NO } : {}),
  ...(params.TRS_TYPE ? { TRS_TYPE: params.TRS_TYPE } : {}),
})

interface SkuBlockState {
  title: string
  message: string
  barcodes: string[]
}

interface DuplicateState {
  items: EtcOutboundDuplicateItem[]
  duplicateCount: number
  cancelCount: number
  /**
   * 서버가 되돌려준 저장 대상 — 강행 시 그대로 itemData 로 다시 보낸다.
   * BE 가 안 주면(`saveData` 빈 배열) 방금 보낸 원본(`EtcOutboundSaveItem[]`)을 대신 쓴다.
   */
  saveData: (EtcOutboundSaveItem | EtcOutboundSavedItem)[]
}

/**
 * GI38 기타출고 — 1.0 GI35 를 신규 디자인(pbo-core)으로 재구현한 화면. (LOGISPRD-11239)
 *
 * 겉(제목·안내 문구·조회영역·표·액션바·엑셀 사유/업로드 모달)은 컨덕터 뷰(`./handoff/page.generated`, 재핸드오프 때 통째로 갱신)가 그리고,
 * 이 파일은 옛 훅·API·세션/URL 유지·잠금·검증을 그대로 두고 그 뷰에 배선(Wiring)으로 넘긴다(컴포넌트 모드, 컨덕터 문서 51).
 * 옛 조립부(`components/OutboundEtcFilter·OutboundEtcTable·ExcelDownloadReasonModal·ExcelUploadModal·PboScope`)가 나눠 갖던 상태를 여기로 모았다 —
 * 표 선택·목록 검색·페이저·모달 열림·필터 폼은 뷰가 가지므로 그 짝(selectedKeys 등)은 지웠다.
 *
 * 티켓 제목의 메뉴코드 GI24 는 오기다. GI24 는 "출고조회 (차수별)_신규" 로 2.0 에 이미 이관돼 있고
 * (routesWithElements `gi24`), 이 화면의 1.0 대응은 GI35 다. 2.0 신규 메뉴코드는 GI38 이며
 * 1.0 GI35 는 iframe 폴백으로 그대로 둔다. 컨덕터 라우트 키는 gi35 다.
 *
 * 조회·저장·확정은 moms-be(2.0) `OutboundEtcController` 를 부른다. 요청·응답이 1.0 그리드 계약
 * (대문자 스네이크 필드 · JSON 문자열 래핑)인 건 BE 가 그렇게 설계돼 있어서다(DTO 주석 참고).
 *
 * 1.0 에서 그대로 옮긴 규칙
 * - 조회는 화주코드가 필수(BE `EtcOutboundValidator.validateSearch` 가 requireNotNull)
 * - 확정 전 전화·핸드폰·우편번호 형식 검증, 저장 전 우편번호 검증 (`GI35.js:156~190`)
 * - 확정 전 배송유형 검증 — 빈 값·허용 목록 밖 값을 막는다(LOGISPRD-11888, `validation.ts`)
 * - 저장 응답에 progressKey 가 있으면 진행률 폴링 모달 · 중복 주문(400)이면 확인 모달 → 사유와 함께 강행 저장 · SKU 미해소(422)면 막힌 바코드 전량 모달
 *
 * 데모 대비 의도적 이탈(근거는 각 지점 주석):
 * - 배송유형·화주유형 옵션은 BE 열거형이다(services consts 주석). 데모 12종 중 긴급(퀵)·수출·택배-특송·기타는 2.0 에 없다.
 * - 화주명 옵션은 기타출고 지원 화주(`ETC_OUTBOUND_AVAILABLE_SHIPPERS`)로 거른다 — 그 밖의 화주는 BE 가 조회부터 400 으로 막는다.
 * - 엑셀 다운로드는 BE 가 마지막 조회 조건으로 다시 뽑아 비밀번호 zip 으로 내려준다(LOGISPRD-15797·15805). 1.0 GI35 도 비밀번호 zip 이었다.
 *   목록 검색어·저장 안 한 입력은 파일에 담기지 않는다. 감사로그(사유)도 BE 가 남긴다.
 * - 생성본을 직접 고쳤다(LOGISPRD-15797) — 엑셀 모달의 '파일 비밀번호' 칸(RGR05 생성본과 같은 요소)·열 때 비우는 필드에 password. 재핸드오프로 덮이면 seam.test 가 잡는다.
 * - [확정] 은 API 전에 확인창(PboConfirm "데이터 송신을 시작 하시겠습니까?")을 거친다 — 데모에는 없지만 되돌릴 수 없는 동작이라 옛 화면의 안전장치를 유지했다(PD 확인 필요).
 * - 중복 주문·SKU 미해소·저장 진행률 모달은 데모에 없는 dev 오류·진행 UI 다.
 * - [삭제] 는 데모에 없다 — DEV QA 요청으로 생성본에 버튼을 직접 넣었다(LOGISPRD-14426). 재핸드오프 전에 데모 반영 필요(컨덕터 VOC).
 * - 화주명 칸은 데모의 멀티 셀렉트가 아니라 단일 `core.Select` 다 — BE `SPR_NM` 이 단건이라 생성본을 직접 고쳤다(LOGISPRD-14805). 재핸드오프 전에 데모 반영 필요(컨덕터 VOC).
 * - 표의 브랜드ID·업체코드·SKUID 열은 데모에 없다 — 1.0(GI35_HEAD)·엑셀 열 구성과 맞추려고 생성본에 직접 넣었다(LOGISPRD-14806). 재핸드오프 전에 데모 반영 필요(컨덕터 VOC).
 */
function OutboundEtcScreen({ coMingle }: { coMingle: boolean }) {
  // 세션 사용자는 마운트 후 바뀌지 않는다 — 렌더마다 sessionStorage 를 다시 읽지 않는다(마운트 스냅샷은 useRef, 문서 51 ⑩).
  const userRef = useRef<ReturnType<typeof AuthStorage.user.get>>()
  userRef.current ??= AuthStorage.user.get()
  const user = userRef.current
  // 화주 잠금(LOGISPRD-14653) — 통합 운영 화주 외 계정은 화주명 옵션이 자기 화주 하나고, 복원·조회 화주도 자기 화주로 강제한다(1.0 FilterBar 규칙).
  //  재고통합 ON 이면 잠긴 29CM·INOOINT 는 자기 화주와 MUSINSA 를 고른다(LOGISPRD-15609, `withCoMingle`).
  const scope = useMemo(() => withCoMingle(shipperScopeOf(user), coMingle), [user, coMingle])
  /** 화주를 고를 수 있는 잠금 계정(재고통합 대상) — 화주유형·화주명 칸을 열고, 화주유형은 고른 화주의 유형을 따른다. */
  const pickable = scope.locked && canSelectShipper(scope)
  const { options: shippers, error: shipperError, errorUpdatedAt: shipperErrorAt } = useGetEtcOutboundShippers()
  /** 화주 목록(get-shippers)의 logisticsServiceType. 목록이 오기 전엔 모른다. */
  const serviceTypeOf = (shipperCode?: string): string | undefined => shippers.find((s) => s.value === shipperCode)?.logisticsServiceType || undefined
  // 잠금 계정의 자기 유형 — 목록이 오기 전엔 모른다(filterFallback 이 늦게 채운다). (LOGISPRD-14949)
  const ownServiceType = scope.locked ? serviceTypeOf(scope.shipperCode) : undefined
  /** 화주명 기본값 — 세션 화주. 재고통합 대상 계정은 MUSINSA 다(LOGISPRD-15718) */
  const defaultShipperCode = defaultShipper(scope).shipperCode
  // 조회영역 기본값·fallback 에 넣는 기본 화주의 유형 — 재고통합 대상 계정은 MUSINSA 의 유형이다(LOGISPRD-15718).
  //  기타출고 미지원 화주는 화주명과 같이 비워 둔다(어차피 [조회]가 막힌다).
  const defaultServiceType = scope.locked ? serviceTypeOf(defaultShipperCode) : undefined
  const lockedDefaultServiceType = defaultServiceType && ETC_OUTBOUND_AVAILABLE_SHIPPERS.includes(defaultShipperCode) ? defaultServiceType : undefined
  // 잠금 계정의 화주유형 옵션 = 고를 수 있는 화주의 유형(대개 자기 유형 하나). 옵션 슬롯은 매 렌더 불리고 뷰의 filterFallback effect 가 옵션 배열 참조를 의존성으로 보므로 참조를 고정한다.
  const lockedServiceTypeOptions = useMemo(() => {
    const types = selectableShippers(scope)
      .filter((s) => !pickable || ETC_OUTBOUND_AVAILABLE_SHIPPERS.includes(s.shipperCode))
      .map((s) => shippers.find((o) => o.value === s.shipperCode)?.logisticsServiceType)
      .filter((t): t is string => Boolean(t))
    return [...new Set(types)].map((t) => ({ value: t, label: t }))
  }, [scope, pickable, shippers])
  // 화주 목록 실패 — 전역 알럿(옛 디자인)을 끄고(훅 meta) 여기서 신규 디자인 알럿으로. 실패 시각이 바뀔 때만 다시 알린다.
  useEffect(() => {
    if (shipperError) notice.failure(MESSAGES.shipperLoadFailed)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipperErrorAt])

  // 조회 조건을 메뉴 세션(sessionStorage)·URL 쿼리에 남겨 다른 메뉴를 다녀와도 유지한다(MOMS 공통 UX).
  // 탭 전환은 react-router 언마운트라 로컬 state 가 통째로 날아간다 — 값을 컴포넌트 밖에 둬야 남는다.
  const menuSession = useMenuOnSession() // reload 를 구조분해하지 않는다(unbound-method)
  const { parsedQs, replaceQuery } = useParsedQuery<EtcOutboundSearchQuery>()
  // 마운트 시 한 번만 복원한다 — 이후 조회로 URL 이 바뀌어도 입력을 덮어쓰지 않는다.
  const restoredRef = useRef<ReturnType<typeof fromSearchQuery>>()
  restoredRef.current ??= fromSearchQuery(parsedQs)
  // 재고통합 대상 계정이 옵션 밖 화주를 복원하면 기본 화주 MUSINSA 로 덮는다. 진입 조회는 MUSINSA 의 유형이 정해질 때까지 미룬다(LOGISPRD-15718 AC-011-05).
  //  덮기 전에 한 번만 판정한다. [조회]·[초기화] 를 먼저 누르면 미룬 조회를 버린다.
  const awaitRestoreTypeRef = useRef<boolean>()
  awaitRestoreTypeRef.current ??= (() => {
    const restoredShipper = restoredRef.current.params.SPR_NM
    return Boolean(scope.locked && restoredShipper && defaultShipperCode !== scope.shipperCode && scopedShipperCode(scope, restoredShipper) !== restoredShipper)
  })()
  // 잠금 계정은 URL 에 고를 수 없는 화주가 남아 있으면 기본 화주로 복원한다. 화주가 없는 복원은 그대로 둔다 — 채우면 '조회 조건 있음' 이 되어 마운트 때 조회가 돈다.
  if (scope.locked && restoredRef.current.params.SPR_NM) restoredRef.current.params.SPR_NM = scopedShipperCode(scope, restoredRef.current.params.SPR_NM)
  // 잠금 계정은 URL 의 화주유형도 버린다 — 칸이 잠겨 있어 고칠 수 없고, 자기 유형은 화주 목록이 온 뒤 filterFallback 이 채운다. 진입 조회도 같은 params 를 쓴다(LOGISPRD-14949).
  if (scope.locked) restoredRef.current.params.LOGISTICS_SERVICE_TYPES = undefined
  const restored = restoredRef.current

  // 미룬 조회는 화주를 비워 둔다 — 화주가 비면 쿼리가 비활성이다(enabled).
  const [searchParams, setSearchParams] = useState<EtcOutboundSearchParams>(() =>
    awaitRestoreTypeRef.current ? { ...restored.params, SPR_NM: undefined } : restored.params,
  )
  useEffect(() => {
    if (!awaitRestoreTypeRef.current || !lockedDefaultServiceType) return
    awaitRestoreTypeRef.current = false
    setSearchParams({ ...restored.params, LOGISTICS_SERVICE_TYPES: [lockedDefaultServiceType] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedDefaultServiceType])
  /**
   * 엑셀 업로드로 교체한 행 — null 이면 조회 결과를 그린다. [삭제] 로 업로드 행을 다 빼도 빈 배열로 남는다(조회 결과로 돌아가지 않는다).
   */
  const [importedRows, setImportedRows] = useState<EtcOutboundEditableRow[] | null>(null)
  /**
   * 편집 셀 입력분 — 키 → 표가 마지막으로 돌려준 행. 한 번이라도 값이 바뀐 행만 담고, 입력마다 다시 그리지 않는다(LOGISPRD-14196).
   * core.Table 은 키 하나마다 전체 행을 넘기는데, 그때마다 화면을 다시 그리면 화면·뷰·표가 다시 그려져 입력이 밀린다.
   * 입력값은 표가 자기 상태로 이미 보여 준다. 읽는 쪽은 `getEffectiveRows` 로 합쳐 읽고, 표가 다시 그려질 일이 생기기 전에 `syncEdits` 로 `rows` 에 얹는다.
   * 담긴 키는 값을 원본으로 되돌려도 빼지 않는다 — 되돌린 행도 저장 대상이다(옛 handleRowsChange 규칙, 아래 mergeEdits).
   * 새로 받기 시작할 때(`discardLocal`)·업로드·초기화에서 비운다 — 나머지 구간이 들어와도 지우지 않아 받는 중 편집이 남는다.
   */
  const editsRef = useRef(new Map<string, Partial<EtcOutboundEditableRow>>())
  /** `syncEdits` 가 올린다 — 입력분을 `rows` 에 다시 얹으라는 신호다. */
  const [editsVersion, setEditsVersion] = useState(0)
  /** 표를 새로 깔 때 올린다 — 뷰가 옛 행 키의 선택을 비운다(wiring.selResetToken). 구간이 들어올 때는 올리지 않는다. */
  const [selResetToken, setSelResetToken] = useState(0)

  const [progressKey, setProgressKey] = useState<string | null>(null)
  /** 진행률 성공 응답 횟수 — 실패는 `errorUpdateCount` 가 세므로 둘을 합쳐 시도 횟수로 본다. */
  const attemptsRef = useRef(0)
  /** 진행률 모달이 맡은 저장이 [삭제] 였으면 그 건수 — 완료로 닫을 때 완료 토스트를 띄운다(BE 저장은 늘 워크플로라 슬롯이 토스트를 못 낸다). */
  const deleteCountRef = useRef(0)
  const [skuBlock, setSkuBlock] = useState<SkuBlockState | null>(null)
  const [duplicate, setDuplicate] = useState<DuplicateState | null>(null)

  const searchBody = useMemo(() => toSearchBody(searchParams), [searchParams])
  // 전량을 받아 뷰가 10,000건씩 나눈다(LOGISPRD-15268). 조회 실패 알럿은 공용 훅 meta(errorToast)로 전역이 띄운다.
  const list = useGetEtcOutbounds(searchBody)
  const { guardAction, refetch } = list
  /** 조회 조건 최신값 — 콜백이 렌더 시점 값에 묶이지 않게(저장 응답을 기다리는 사이 조건이 바뀌었을 수 있다). */
  const latestRef = useRef({ searchParams, searchBody })
  latestRef.current = { searchParams, searchBody }
  const fetchedRows = useMemo(() => toEditableRows(list.rows), [list.rows])
  const { onDownloadExcel } = useExcelDownload<EtcOutboundDownloadParams>({ config: { method: 'post' } })
  /** 조회 응답 원본 행 — 바코드를 고쳤다 되돌렸는지 가리는 기준이다. 업로드 행(`upload-N`)은 여기 없다. */
  const originalByKey = useMemo(() => new Map(fetchedRows.map((row) => [row.key, row])), [fetchedRows])
  /**
   * `rows` 에 입력분을 얹는다(옛 handleRowsChange 의 행 병합).
   * SKU_ID 는 조회 응답에서만 채워지는 읽기 전용 칸이다. 바코드가 원본과 다르면 옛 바코드의 SKU_ID 가
   * 남지 않게 비우고, 원본 바코드로 되돌리면 원본 SKU_ID 를 되살린다. 원본이 없는 신규 행은 그대로 둔다.
   * 'E' 는 BE isUpdate() 가 보는 값이다. 'U' 로 두면 저장이 조용히 0건으로 끝난다. 업로드 행('I')은 그대로 'I'.
   * 입력분에 담긴 행은 값을 원본으로 되돌려도 'E' 다 — 되돌린 행도 저장 대상(옛 handleRowsChange 와 같은 규칙).
   */
  const mergeEdits = (base: EtcOutboundEditableRow[]): EtcOutboundEditableRow[] =>
    base.map((before) => {
      const patch = editsRef.current.get(before.key)
      if (!patch) return before
      // 입력분은 보통 표가 넘긴 행 전체지만, 같은 조건 재조회 뒤에는 그 사이 고친 칸만 남는다(discardLocal) — 그래서 원래 행 위에 덮는다.
      const edited = { ...before, ...patch }
      const original = originalByKey.get(before.key)
      const skuId = original && (edited.BARCODE === original.BARCODE ? original.SKU_ID : null)
      const row = original && edited.SKU_ID !== skuId ? { ...edited, SKU_ID: skuId ?? null } : edited
      return { ...row, EDITTED: before.EDITTED ?? ('E' as const) }
    })
  /**
   * 표의 행 = 업로드 행 또는 받은 행, 그 위에 입력분. 받은 행을 로컬 state 로 옮기지 않는다 — 구간이 들어올 때마다 옮기면
   * 그 사이 입력이 지워지고, 같은 응답이면 react-query 가 옛 참조를 돌려줘(structuralSharing) 옮기는 시점도 못 잡는다.
   * 버리는 시점은 응답이 아니라 동작(조회·저장 뒤 재조회·업로드·초기화)이 정한다.
   */
  const rows = useMemo(
    () => {
      const base = importedRows ?? fetchedRows
      return editsRef.current.size ? mergeEdits(base) : base
    },
    // editsVersion = syncEdits 신호. mergeEdits 는 editsRef·originalByKey(fetchedRows 에서 나온다)만 읽는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [importedRows, fetchedRows, editsVersion],
  )
  const getEffectiveRows = (): EtcOutboundEditableRow[] => (editsRef.current.size ? mergeEdits(rows) : rows)
  /** 메모한 콜백(runSearch)이 렌더 시점 행에 묶이지 않게 최신 함수를 둔다. */
  const getEffectiveRowsRef = useRef(getEffectiveRows)
  getEffectiveRowsRef.current = getEffectiveRows
  /** 입력분을 `rows` 에 얹는다 — 같은 값을 다시 얹을 뿐이라 여러 번 불러도 같다. ref 는 다음 교체 때 비운다. */
  const syncEdits = (): void => {
    if (editsRef.current.size) setEditsVersion((n) => n + 1)
  }
  const { mutateAsync: save, isPending: isSaving } = useSaveEtcOutbounds()
  const { mutateAsync: confirm, isPending: isConfirming } = useConfirmEtcOutbounds()
  const {
    data: progress,
    isError: isProgressError,
    errorUpdateCount: progressErrorCount,
    dataUpdatedAt: progressUpdatedAt,
  } = useGetOutboundProgress(progressKey)

  /**
   * 저장이 반영되는 동안에는 확정을 잠근다.
   *
   * 저장은 워크플로우로 비동기 처리되고 `progressKey` 가 살아 있는 동안은 아직 반영 중이다.
   * 그 사이 확정을 누르면 반영이 안 끝난 주문이 컷오프 차수에 실려 Jenkins 파이프라인으로 나간다.
   * 되돌릴 수 없는 부작용이라 버튼 단계에서 막는다. 교차 잠금(확정 중 저장)도 같은 이유 — 뷰의 선택 게이트 버튼이 `useBusy` 로 둘 다 잠근다.
   */
  const isSaveInFlight = isSaving || progressKey !== null
  // 첫 구간을 받는 동안 잠근다 — 표가 통째로 바뀌어 그 사이 고른 행이 다른 행이 될 수 있다. 나머지 구간은 행이 뒤에 붙기만 해서 잠그지 않고,
  //  저장·확정·삭제·엑셀은 각 슬롯 첫 줄 guardAction 이 막는다.
  const isBusy = isSaveInFlight || isConfirming || list.isFirstLoading

  // 편집 셀 화주코드도 기타출고 지원 화주만 — 그 밖의 화주는 BE 가 저장을 400 으로 거부한다(조회영역 화주명 후보와 같은 기준, 봇 리뷰 #801).
  const shipperOptions = useMemo(
    () => shippers.filter((s) => ETC_OUTBOUND_AVAILABLE_SHIPPERS.includes(s.value)).map(({ value, label }) => ({ value, label })),
    [shippers],
  )

  // 센터명(WH_NM)·다운로드 사유코드(DOWNLOAD_CD) — dev 실측을 박아 두던 화면 상수를 API 응답으로
  // 교체한다(LOGISPRD-12385). 배송유형은 MOMS_COMBO_INFO 가 아니라 moms-be TransportType enum 이
  // 정본이라(services/outbound/outboundEtc/consts.ts 주석) 그대로 둔다.
  const { data: combos } = useGetCombos(['WH_NM', 'DOWNLOAD_CD'], { errorToast: true })
  // WH_NM 의 'ALL'(전체) 행은 편집 셀에 실려도 저장 가능한 실제 센터가 아니다(리뷰 반영).
  const warehouseNameOptions = useMemo(() => toSelectOptions(excludeAllOption(combos?.WH_NM)), [combos])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  /** 저장·확정은 단일 화주 기준이다 — 조회 조건의 화주코드가 없으면 첫 행의 화주를 쓴다(1.0 동일). */
  const resolveShipperCode = useCallback(
    (targetRows: EtcOutboundEditableRow[]) => searchParams.SPR_NM || targetRows[0]?.SPR_NM || defaultShipperCode,
    [searchParams.SPR_NM, defaultShipperCode],
  )

  /**
   * 새로 받기 시작 — 입력분·업로드 행을 버린다(그게 [조회]·저장 뒤 재조회의 뜻이다). 업로드 행을 남기면 저장 뒤에도 'I' 로 남아 같은 주문이 다시 INSERT 된다.
   * `only`([조회] 때 보이던 행)를 주면 그때 있던 입력분만 버린다 — 재조회를 기다리는 사이 새로 입력한 칸은 남긴다(keepEditedSince).
   * `editsVersion` 도 올린다 — 같은 응답이면 `fetchedRows` 참조가 그대로라 `rows` 메모가 다시 계산되지 않아 얹어 둔 입력이 남는다.
   */
  const discardLocal = useCallback((only?: Map<string, EtcOutboundEditableRow>) => {
    if (only) keepEditedSince(editsRef.current, only)
    else editsRef.current.clear()
    setImportedRows(null)
    setEditsVersion((n) => n + 1)
    setSelResetToken((n) => n + 1)
  }, [])
  /** [조회] 차례 — 늦게 끝난 앞 조회가 뒤 조회의 입력분을 버리지 않게 비교한다. */
  const searchSeqRef = useRef(0)

  /** 저장·확정·삭제·업로드 첫 줄 — 받는 중·일부 실패면 guardAction 이 알리고, throw 로 뷰가 선택·모달을 그대로 둔다(뷰 계약). */
  const guardOrThrow = (): void => {
    if (!guardAction()) throw markShown(new Error(FETCH_ALL_MESSAGES.fetching))
  }

  const runSearch = useCallback(
    async (params: EtcOutboundSearchParams, { notifyEmpty = true } = {}) => {
      const nextKey = outboundEtcQueryKeys.list(toSearchBody(params))
      // 조건이 같으면 지금 행이 그대로 남는다 — 입력분·업로드 행은 다시 받기에 성공한 뒤 버린다(실패하면 남아 입력이 보인다).
      //  조건이 다르면 표가 새 조건의 행으로 바뀌므로 지금 버린다(옛 조건 행의 입력이 새 행에 얹히지 않게).
      awaitRestoreTypeRef.current = false
      const sameKey = hashKey(nextKey) === hashKey(outboundEtcQueryKeys.list(latestRef.current.searchBody))
      if (!sameKey) discardLocal()
      const seq = ++searchSeqRef.current
      // 업로드 행('I')은 재조회 성공 때 버려진다 — 그 행의 입력분도 남기지 않게 기준에서 뺀다.
      const shownAtSearch = new Map(getEffectiveRowsRef.current().filter((row) => row.EDITTED !== 'I').map((row) => [row.key, row]))

      // queryParams 커밋 전에 조회하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다.
      // (뷰의 [조회] onClick 에서 동기로 불리는 자리라 flushSync 를 써도 된다.)
      flushSync(() => {
        setSearchParams(params)
      })

      // 같은 조건이어도 누를 때마다 처음 구간부터 다시 조회한다(1.0 동일, gi37 과 같다).
      //  같은 조건은 `cancelRefetch: true` 여야 한다 — false 면 나머지 구간을 받는 중일 때 그 묶음(fetchNextPage)에 합류할 뿐이라
      //  다시 받지 않은 채 아래에서 입력분만 버린다(query-core `query.js:142`). 연타하면 앞 요청이 취소되지만 서버까지는 간다(gi37 과 같은 비용).
      //  조건이 바뀌었으면 키가 달라져 이미 받기 시작했다 — 받은 data 가 없어 취소 없이 거기 합류한다.
      const res = await refetch({ cancelRefetch: sameKey })
      if (res.isError) return // 실패 알럿은 전역(공용 훅 meta)이 띄운다
      if (seq !== searchSeqRef.current) return // 그 사이 다시 조회했다 — 입력분·안내는 뒤 조회가 정한다
      if (sameKey) discardLocal(shownAtSearch)
      // 0건 안내 — 사용자가 누른 조회·저장 뒤 재조회만(복원 진입의 자동 조회는 이 경로를 타지 않는다). BE 는 0건을 rtnChk:false + 안내 문구로 주고 서비스가 빈 결과로 바꾼다.
      //  확정 직후 재조회는 끈다(notifyEmpty) — 조회는 미확정만 돌려주므로 전량 확정이면 0건이 정상이다(LOGISPRD-14428).
      if (notifyEmpty && (res.data?.[0]?.totalCount ?? 0) === 0) notice.failure(MESSAGES.searchEmpty)
    },
    [refetch, discardLocal],
  )

  /** 조회 조건을 메뉴 세션·URL 에 남긴다. 셋을 함께 부르는 게 레포 규약이다. */
  const persistSearch = useCallback(
    (params: EtcOutboundSearchParams, nextPage: number, nextKeyword: string) => {
      const query = toSearchQuery({ params, keyword: nextKeyword, page: nextPage })
      MenuStorage.menuOnSession.setSearch(query)
      menuSession.reload()
      replaceQuery(query)
    },
    [menuSession, replaceQuery],
  )

  /**
   * 저장·확정 뒤 목록 갱신. 아직 한 번도 조회하지 않았으면 아무것도 하지 않는다 —
   * 엑셀 업로드로 바로 저장한 경우가 그렇다. 그냥 재조회를 부르면 "화주명은 필수입니다." 가 뜬다.
   * 조건은 ref 로 최신값을 읽는다(latestRef).
   */
  const refreshCurrentSearch = useCallback(async ({ quietEmpty = false } = {}) => {
    const latest = latestRef.current.searchParams
    if (!latest.SPR_NM) return
    // 저장·확정은 끝났다 — 재조회 성공을 기다리지 않고 버린다. 실패해 업로드 행('I')이 남으면 다시 [저장] 할 때 같은 주문이 또 들어간다.
    discardLocal()
    await runSearch(latest, { notifyEmpty: !quietEmpty })
  }, [runSearch, discardLocal])

  /** 저장·강행 저장이 같은 응답 분기를 쓴다. 알린 뒤 throw 한다(뷰가 선택을 유지). */
  const handleSaveError = useCallback((error: unknown, targetItems: (EtcOutboundSaveItem | EtcOutboundSavedItem)[]): never => {
    const body = extractSaveError(error)

    if (body?.errorCode === ERROR_OUTBOUND_SKU_UNMAPPED) {
      setSkuBlock({ title: MESSAGES.saveFailedTitle, message: body.message ?? '', barcodes: body.failedBarcodes ?? [] })
      throw markShown(error)
    }

    if (body?.dupCheck) {
      setDuplicate({
        items: body.dupData ?? [],
        duplicateCount: body.dupCount ?? 0,
        cancelCount: body.cancelCount ?? 0,
        // 서버가 saveData 를 주지 않으면 방금 보낸 항목을 그대로 다시 쓴다.
        saveData: body.saveData?.length ? body.saveData : targetItems,
      })
      throw markShown(error)
    }

    throw markShown(failureError(extractMessage(error, MESSAGES.saveFailed)))
  }, [])

  /**
   * 저장 요청. 성공 문구를 돌려준다(뷰가 토스트). progressKey 가 오면 진행률 모달이 결과를 맡으므로 `false`(토스트 없음).
   * 실패는 throw — 슬롯에서 부르면 뷰가 선택을 유지하고, 중복 모달에서 부르면 모달 쪽이 삼킨다.
   */
  const submitSave = useCallback(
    async (items: (EtcOutboundSaveItem | EtcOutboundSavedItem)[], remark?: string): Promise<string | false> => {
      const shipperCode = items[0]?.SPR_NM || resolveShipperCode(rows)
      const shipperName = shippers.find((s) => s.value === shipperCode)?.label ?? shipperCode
      // 지난 [삭제] 가 실패로 끝났으면 남아 있을 수 있다 — 이번 저장 완료에 삭제 토스트가 붙지 않게 비운다.
      deleteCountRef.current = 0
      let res: EtcOutboundSaveRes
      try {
        res = await save(toSaveBody({ items, shipperCode, shipperName, remark }))
      } catch (error) {
        return handleSaveError(error, items)
      }
      // 동기 단계 실패(검증 예외 등)는 200 + rtnChk:false 로 온다(BE `ProcessEtcOutboundResponseBody.failure`) — 성공으로 넘기면 [삭제]·[저장] 이 완료 토스트를 띄운다.
      if (!res.rtnChk && !res.progressKey) throw markShown(failureError(res.message || MESSAGES.saveFailed))
      if (res.progressKey) {
        attemptsRef.current = 0
        setProgressKey(res.progressKey)
        return false
      }
      await refreshCurrentSearch()
      return res.message || MESSAGES.saveDone
    },
    [rows, shippers, save, resolveShipperCode, handleSaveError, refreshCurrentSearch],
  )

  /** 중복 주문 확인 모달 [진행] — 사유와 함께 강행 저장. 슬롯 밖이라 토스트도 여기서 띄운다. */
  const handleDuplicateProceed = useCallback(
    (remark: string) => {
      const items = duplicate?.saveData ?? []
      setDuplicate(null)
      submitSave(items, remark)
        .then((message) => {
          if (message) notice.success(message)
        })
        .catch(() => undefined) // 실패는 handleSaveError 가 이미 알렸다(알럿·모달)
    },
    [duplicate, submitSave],
  )

  // 상태가 아직 RUNNING 이어도 진행률이 100 에 닿으면 끝난 것으로 본다(1.0 progress >= 100) — 모달은 COMPLETED 여야 [확인]을 풀어 준다.
  // FAILED 는 진행률과 무관하게 실패다(1.0 은 FAILED 를 먼저 본다). 일반출고(generalOutbound/index.tsx)와 같은 규칙.
  const isProgressDone = Boolean(
    progress &&
      progress.status !== PROGRESS_FAILED &&
      (progress.status === PROGRESS_COMPLETED || (progress.total > 0 && progress.processed >= progress.total)),
  )
  const shownProgress = progress && isProgressDone ? { ...progress, status: PROGRESS_COMPLETED } : (progress ?? null)

  const handleProgressClose = useCallback(async () => {
    // 실패(아래 effect)로 닫혀도 불린다 — 완료일 때만 알린다.
    const deleted = isProgressDone ? deleteCountRef.current : 0
    deleteCountRef.current = 0
    setProgressKey(null)
    await refreshCurrentSearch()
    if (deleted) notice.success(MESSAGES.deleteDone(deleted))
  }, [refreshCurrentSearch, isProgressDone])

  // 성공 응답이 갱신될 때마다 시도 횟수를 센다.
  useEffect(() => {
    if (progressKey && progressUpdatedAt) attemptsRef.current += 1
  }, [progressKey, progressUpdatedAt])

  /**
   * 진행률이 실패로 끝나면 알럿을 띄우고 모달을 닫는다. 1.0 progressPollingEpic 동치 —
   * status FAILED 이거나 조회가 연속으로 상한만큼 실패하면 사용자에게 알리고 폴링을 접는다.
   * 훅은 이미 그 조건에서 폴링을 멈추므로 여기선 화면만 정리한다.
   */
  useEffect(() => {
    if (!progressKey) return
    const failed = progress?.status === PROGRESS_FAILED
    // 실패 횟수는 `errorUpdateCount` 로 본다. `failureCount`(= query-core `fetchFailureCount`)는
    // 요청이 시작될 때마다 0 으로 되돌아가고 전역 `retry: 0` 이라 폴링 1회당 최대 1 이다 — 상한이 영원히 안 선다.
    const givenUp = isProgressError && progressErrorCount >= PROGRESS_MAX_FAILURES
    // 시도 상한(10분)에 닿았는데 아직 끝나지 않았으면 폴링을 접는다. 워커가 죽어 RUNNING 200 이 계속 오면 위 두 조건은 걸리지 않는다.
    const timedOut = attemptsRef.current + progressErrorCount >= PROGRESS_MAX_ATTEMPTS && !isProgressDone
    if (!failed && !givenUp && !timedOut) return

    notice.failure(progress?.message || MESSAGES.progressFailed)
    handleProgressClose().catch(() => undefined)
  }, [progressKey, progress, isProgressError, progressErrorCount, progressUpdatedAt, isProgressDone, handleProgressClose])

  /** 표에 실을 행 — 행 키(`key`)는 이미 있다(toEditableRows·업로드). 뷰 기본은 배열 인덱스라 재조회·페이지 이동 뒤 선택이 어긋난다. */
  /**
   * 업로드 행('I')에는 `__new` 를 단다 — core.Table 은 사용자가 만든 행만 처음부터 필수 칸을 검사해 빈 칸이 있으면 행을 빨갛게 칠한다(DS 계약).
   * 표시용 표식이라 상태(rows)에는 넣지 않는다 — 저장 본문(payload.ts)이 행을 펼쳐 보내므로 편집 콜백에서 다시 뗀다.
   */
  const viewRows = useMemo(() => rows.map((r) => (r.EDITTED === 'I' ? withNewMarker(r) : r)) as unknown as Row[], [rows])

  /** 키 → 지금 `rows` 의 행 — 편집 콜백이 입력분이 원본과 다른지 가린다(키 입력마다 불려 전량을 매번 훑지 않게). */
  const rowsByKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows])
  /**
   * 선택 행 → 현재 편집 상태의 행. 뷰가 넘기는 `ctx.selected` 는 선택 당시의 행 객체라 그 뒤 편집 셀로 바뀐 값이
   * 없을 수 있다 — 키로 지금 상태의 행을 다시 집는다(전량 × 선택수만큼 훑지 않게 Set 색인).
   */
  const pickSelected = (ctx: ActionCtx): EtcOutboundEditableRow[] => {
    const keys = new Set(ctx.selected.filter((r) => typeof r.key === 'string').map((r) => r.key as string))
    return getEffectiveRows().filter((r) => keys.has(r.key))
  }
  /** 화면에 보이는 행(목록 검색을 거친 뒤) → 현재 편집 상태의 행 — 엑셀 다운로드 대상. `visible` 이 없는 뷰면 전체 행이다. */
  const pickVisible = (ctx: ActionCtx): EtcOutboundEditableRow[] => {
    const current = getEffectiveRows()
    if (!ctx.visible) return current
    const keys = new Set(ctx.visible.filter((r) => typeof r.key === 'string').map((r) => r.key as string))
    return current.filter((r) => keys.has(r.key))
  }

  /**
   * 화주명 후보 — 기타출고를 지원하는 화주로 거른다(BE `EtcOutboundValidator` 가 그 밖의 화주를 조회 단계부터 400 으로 막는다).
   * 그다음 조회영역에 고른 화주유형으로 좁힌다(유형 미선택·'전체' 면 지원 화주 전체). 뷰가 옵션 슬롯에 현재 조회영역 값을 넘겨 준다.
   * ⚠ 옛 필터는 유형을 바꿔 후보에서 사라진 화주코드를 폼에서 지웠는데, 조회영역 값은 뷰 소유라 여기서 못 지운다 — 조회 때 `SPR_NM` 으로
   *   그대로 나가면 BE 가 400 이 아니라 조용히 0건을 준다. TODO(conductor): 옵션이 좁아졌을 때 값도 거르는 규칙.
   */
  const shipperNameOptionsFor = useCallback(
    (filters?: Record<string, unknown>) => {
      const types = asList(filters?.['etcOutShipper.types'])
      // 잠금 계정은 고를 수 있는 화주만(대개 자기 화주, 재고통합 대상은 MUSINSA 까지) — 지원 화주 필터와 둘 다 적용한다. 남는 화주가 없으면 후보가 비고 [조회] 가 막힌다(BE 도 400).
      const selectable = selectableShippers(scope).map((s) => s.shipperCode)
      const supported = shippers.filter((s) => ETC_OUTBOUND_AVAILABLE_SHIPPERS.includes(s.value) && (!scope.locked || selectable.includes(s.value)))
      const source = types.length ? supported.filter((s) => types.includes(s.logisticsServiceType)) : supported
      return source.map(({ value, label }) => ({ value, label }))
    },
    [shippers, scope],
  )

  /**
   * 페이지 이동·목록 검색어 통지(뷰 onState). 뷰는 마운트 직후에도 한 번 부른다 — 복원값 그대로면 URL·세션을 다시 쓰지 않는다.
   * 페이지 이동 판정은 뷰가 마지막으로 알린 페이지 기준이다(gi31 #798 리뷰).
   */
  const lastViewRef = useRef({ page: restored.page, keyword: restored.keyword })
  /** 받는 중에 온 뷰 통지(페이지·목록 검색어) — 받기가 끝나면 처리한다(아래 flush effect, LOGISPRD-15349) */
  const pendingViewRef = useRef<ViewState | null>(null)

  // ── 뷰 배선(Wiring) — 옛 핸들러의 본문을 슬롯에 잇는다. 필터 폼·표 선택·목록 검색·페이저·모달 열림은 뷰가 가진다. ──
  //  배선 객체는 매 렌더 새로 만든다(뷰가 렌더 첫머리에 props.wiring 을 모듈 홀더에 대입한다 — 문서 51 ⑪).
  /** 페이지 이동 확인창이 떠 있는가 — 대기 중 재클릭을 막는다. */
  const leaveConfirmOpenRef = useRef(false)
  /**
   * 저장하지 않은 편집·업로드 행이 있으면 버려도 되는지 묻는다 — 페이지 이동·[초기화]·메뉴 이동이 같이 쓴다(LOGISPRD-14801). 없으면 묻지 않는다.
   * 신규 디자인 확인창(PboConfirm)으로 묻는다 — 생성본이 Promise 답을 기다렸다 진행한다(pbo-design 페이지 이동 가드 비동기화).
   * 확인창 대기 중 재클릭은 무시한다 — 확인창이 겹치고 두 답이 모두 진행을 돌리지 않게(#899 리뷰, 합배송출고·모니터링과 같은 가드).
   */
  const confirmDiscard = (message: string, okText: string): boolean | Promise<boolean> => {
    if (!editsRef.current.size && !rows.some((row) => row.EDITTED)) return true
    if (leaveConfirmOpenRef.current) return false
    leaveConfirmOpenRef.current = true
    return new Promise<boolean>((resolve) => {
      const answer = (ok: boolean) => {
        leaveConfirmOpenRef.current = false
        resolve(ok)
      }
      PboConfirm.show(message, { onConfirm: () => answer(true), onClose: () => answer(false), okText })
    })
  }
  // 상단 탭·왼쪽 메뉴로 떠나면 화면이 언마운트되어 행이 사라진다 — 떠나기 전에 묻는다.
  useLeaveGuard(() => confirmDiscard(MESSAGES.leaveUnsaved, '이동'))
  /** 페이지 이동·목록 검색어 확정을 받아 둔다(onState 본문 — 받는 중 미룬 통지도 여기로 온다). */
  const applyViewState = (s: ViewState) => {
    const next = s.pager.pager
    const nextKeyword = s.listq['table.q']
    if (lastViewRef.current.page === next.page && lastViewRef.current.keyword === nextKeyword) return
    // 페이지를 옮기면 뷰가 검색어를 '' 로 함께 보낸다 — 검색어가 남은 채 페이지만 바뀐 것은 행이 줄어 뷰가 마지막 페이지로 맞춘 것이라 아래 검색어 경로로 간다(LOGISPRD-15349).
    if (next.page !== lastViewRef.current.page && nextKeyword === '') {
      // 전량을 받아 뒀으므로 다시 조회하지 않는다. 뷰는 페이지를 넘기며 검색칸을 비운다.
      lastViewRef.current = { page: next.page, keyword: '' }
      // 뷰가 표를 다시 마운트했다 — 입력값이 다음에 돌아와도 보이게 넣는다.
      syncEdits()
      // 보던 페이지도 남긴다 — 조건만 남고 1페이지로 돌아가면 사람은 새로고침됐다고 느낀다. 저장할 조건은 **실제로 조회에 쓰인** 조건이다.
      if (searchParams.SPR_NM) persistSearch(searchParams, next.page, '')
      return
    }
    lastViewRef.current = { page: next.page, keyword: nextKeyword }
    // 검색어가 바뀌면 표에 들어가는 행이 바뀌어 표가 자기 상태를 `rows` 로 되돌린다 — 입력분을 먼저 넣어 둔다.
    syncEdits()
    // 목록 검색어도 남긴다 — 조건만 남고 검색어가 풀리면 돌아왔을 때 다른 목록으로 보인다.
    if (searchParams.SPR_NM) persistSearch(searchParams, next.page, nextKeyword)
  }
  // 받기가 끝나면 받는 중 미뤄 둔 통지를 처리한다 — 끝난 렌더에서 뷰가 다시 알렸으면 onState 가 먼저 비워 두어 여기선 할 일이 없다(자식 effect 가 먼저 돈다).
  useEffect(() => {
    if (list.isFirstLoading || list.isFetchingRest || pendingViewRef.current == null) return
    const pending = pendingViewRef.current
    pendingViewRef.current = null
    applyViewState(pending)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 받기가 끝나는 순간에만 돈다(applyViewState 는 매 렌더 새로 만든다)
  }, [list.isFirstLoading, list.isFetchingRest])

  const wiring: Wiring = {
    permission: usePermissionSlot('gi38'),
    // 개인 컬럼 배치 저장 — 메인 표만(모달 안 표는 저장하지 않는다)
    columnLayout: { etcOutTable: coreTableLayoutSlot('GI38_HEAD') },
    rows: {
      // 전량 + 클라이언트 페이징 — total 을 주지 않아 '전체 N'·페이지 수가 받은 만큼 늘어난다(받는 중 진행 표시, LOGISPRD-15267 규약).
      //  받는 동안(첫 구간·나머지 구간·재조회)은 받은 행 위에 로더를 띄워 표를 막는다(LOGISPRD-15390).
      ROWS: () => ({ rows: viewRows, loading: list.isFirstLoading || list.isFetchingRest }),
    },
    actions: {
      /**
       * [조회] — 조회영역 값(생성본 키)을 옛 필터 컴포넌트와 같은 변환으로 BE 조건으로 만든다.
       * 화주코드가 없으면 안내(토스트) + throw — 뷰는 throw 면 페이저·선택·검색칸을 되돌리지 않는다(옛 화면도 알럿만 띄우고 끝났다).
       * 검증이 먼저다 — 화주 없이 [조회] 를 한 번 누른 것만으로 저장돼 있던 조건이 지워지면 안 된다.
       */
      search: async (filters) => {
        // async 라 화면 검증의 throw 가 거부(reject)로 돌아간다 — 동기 throw 면 뷰의 onClick 에서 잡히지 않는다.
        // (첫 await 전까지는 동기로 도니 runSearch 의 flushSync 도 클릭 핸들러 안에서 실행된다.)
        // 잠금 계정은 화주코드를 고를 수 있는 화주로, 화주유형을 그 화주의 유형으로 — 칸이 잠겨 있어도 URL 복원 등 다른 경로의 값이 남을 수 있다.
        //  재고통합 대상이 MUSINSA 를 고르면 화주유형도 MUSINSA 의 유형으로 나간다(LOGISPRD-15609).
        const raw = toParams(filters)
        const lockedShipper = scope.locked ? scopedShipperCode(scope, raw.SPR_NM) : undefined
        const lockedType = pickable ? serviceTypeOf(lockedShipper) : ownServiceType
        const params = scope.locked ? { ...raw, SPR_NM: lockedShipper, LOGISTICS_SERVICE_TYPES: lockedType ? [lockedType] : undefined } : raw
        // BE `EtcOutboundValidator.validateSearch` 는 화주코드를 requireNotNull 로 본다 — 화주유형만
        // 골라 보내면 "화주사 코드가 필요합니다." 로 떨어진다. 요청 DTO 의 @AssertTrue 보다 이쪽이 좁다.
        if (!params.SPR_NM) throw markShown(invalidError(MESSAGES.shipperRequired))
        // 화주유형을 바꿔 후보에서 빠진 화주코드가 조회영역에 남아 있을 수 있다(옛 필터는 폼에서 지웠고, 뷰는 값을 안 지운다) — 그대로 보내면 BE 가 400 이 아니라 조용히 0건.
        if (!shipperNameOptionsFor(filters).some((o) => o.value === params.SPR_NM)) throw markShown(invalidError(MESSAGES.shipperTypeMismatch))

        lastViewRef.current = { page: 1, keyword: '' }
        pendingViewRef.current = null
        // 뷰가 표를 다시 마운트한다 — 같은 조건 재조회가 실패해 목록이 그대로 남아도 입력값이 보이게 먼저 넣는다.
        syncEdits()
        persistSearch(params, 1, '')
        await runSearch(params)
      },
      /**
       * 편집 셀 — 표는 보이는 행(목록 검색으로 걸러진 뒤)만 돌려준다. 그대로 쓰면 가려진 행이 사라지므로 키로 합친다.
       * 편집한 행만 저장 대상이다 — 조회 원본과 비교해 EDITTED 를 세운다(옛 handleRowsChange).
       */
      ROWS_rowsChange: (changed) => {
        // 키 입력마다 불린다 — `rows` 에 넣지 않고 ref 에만 둔다(editsRef 주석). 한 번 담긴 키는 값이 원본과 같아져도 계속 갱신한다.
        changed.forEach((r) => {
          if (typeof r.key !== 'string') return
          const edited = withoutNewMarker(r) as unknown as EtcOutboundEditableRow
          const before = rowsByKey.get(r.key)
          const changedCell = !before || Object.keys(edited).some((k) => k !== 'EDITTED' && edited[k as keyof typeof edited] !== before[k as keyof typeof before])
          if (changedCell || editsRef.current.has(r.key)) editsRef.current.set(r.key, edited)
        })
      },
      /**
       * [저장] — 선택한 행 가운데 편집한 행을 보낸다. 1.0 CommonUtil.onSaveData 의 화면 검증(필수 칸·우편번호)을 서버 왕복 전에 잡는다.
       * 데모의 [저장] 은 선택 행 대상(disableWithoutSelection)이라 옛 화면(선택과 무관하게 편집 행 전부)과 달리 **선택 ∩ 편집** 이다 — PD 확인 필요.
       * 뷰 계약(컨덕터 #582): 실패는 throw(선택 유지) · false = 성공인데 토스트 없음(진행률 모달이 결과를 맡음) · 문자열 = 그 문구로 토스트.
       */
      etcOutSaveButton: async (ctx): Promise<string | false> => {
        guardOrThrow()
        // 저장이 끝나면 뷰가 표를 다시 마운트한다(선택 해제) — 그때 입력분이 `rows` 에 없으면 옛 값으로 돌아간다.
        syncEdits()
        const selected = pickSelected(ctx)
        const edited = selected.filter((row) => row.EDITTED)
        if (!edited.length) throw markShown(invalidError(MESSAGES.noEditedRows))
        if (edited.length > ETC_OUTBOUND_MAX_SAVE_SIZE) throw markShown(invalidError(MESSAGES.saveSizeExceeded))

        // 표 헤더에 별표가 붙은 칸을 전수 훑는다 — 없으면 사용자는 필수로 아는 칸을 비운 채 저장하고 BE 400 으로 튕긴다.
        const missing = findMissingRequired(edited)
        if (missing) {
          // 편집한 행만 걸러 검사하므로 그 안의 순번은 화면 행 번호와 다르다. 화면 NO 는 목록 검색으로 걸러진 뒤 순서라 보이는 행(ctx.visible)에서 찾고, 없으면 전체 행에서.
          const shown = pickVisible(ctx)
          const shownIndex = shown.findIndex((row) => row.key === missing.key)
          const rowNumber = (shownIndex >= 0 ? shownIndex : getEffectiveRows().findIndex((row) => row.key === missing.key)) + 1
          throw markShown(rowInvalidError(MESSAGES.requiredMissing({ rowNumber, title: missing.title, particle: subjectParticle(missing.title) })))
        }

        // 1.0 onHandleSave 는 형식 검증으로 우편번호만 본다.
        if (findInvalidZipCodeRow(edited)) throw markShown(invalidError(MESSAGES.zipCodeInvalid))

        return submitSave(edited.map(toSaveItem))
      },
      /**
       * [삭제] — 선택 행을 확인창을 거쳐 바로 지운다(일반입고 2.0 [삭제] 와 같은 방식, LOGISPRD-14426).
       * 전용 API 가 없어 저장 API 에 EDITTED 'D' 행으로 보낸다(1.0 GI35 ToolBar 삭제 → 저장과 같은 계약). BE 는 행 `id` 로만 지우고,
       * 출고확정된 주문이 섞이면 "이미 출고확정된 주문이 있습니다." 로 전량 거절한다. 값은 편집 전 조회 원본을 싣는다(지울 행의 편집값 검증에 걸리지 않게).
       * 미저장 업로드 행('I')은 id 가 없어 표에서만 뺀다. 확인창을 닫으면 throw — 뷰가 선택을 그대로 둔다.
       */
      etcOutDeleteButton: async (ctx): Promise<string | false> => {
        guardOrThrow()
        syncEdits()
        const targets = pickSelected(ctx)
        if (!targets.length) throw markShown(invalidError(MESSAGES.noSelectedRows))
        const saved = targets.filter((row) => row.EDITTED !== 'I').map((row) => originalByKey.get(row.key) ?? row)
        if (saved.length > ETC_OUTBOUND_MAX_SAVE_SIZE) throw markShown(invalidError(MESSAGES.deleteSizeExceeded))

        await new Promise<void>((resolve, reject) => {
          PboConfirm.show(MESSAGES.deleteConfirm(targets.length), {
            onConfirm: resolve,
            onClose: () => reject(markShown(new Error(MESSAGES.deleteCancelled))),
            okText: '삭제',
          })
        })

        if (saved.length) {
          const result = await submitSave(saved.map((row) => ({ ...toSaveItem(row), EDITTED: 'D' as const })))
          if (result !== false) return MESSAGES.deleteDone(saved.length)
          // 워크플로로 넘어갔다 — 진행률 모달을 완료로 닫을 때 알린다(handleProgressClose).
          deleteCountRef.current = saved.length
          return false
        }
        const keys = new Set(targets.map((row) => row.key))
        keys.forEach((key) => editsRef.current.delete(key))
        // 여기 오는 행은 모두 업로드 행('I')이다 — 받은 행에는 id 가 있다.
        setImportedRows((prev) => prev && prev.filter((row) => !keys.has(row.key)))
        return MESSAGES.deleteDone(keys.size)
      },
      /**
       * [확정] — 선택 행. 화면 검증(건수·배송유형·전화·우편번호·미저장) → 확인창 → API.
       * 확정은 화면에 편집 중인 값이 아니라 BE 에 저장된 값을 쓴다(`toConfirmBody` 는 화주코드·주문번호·주문일련번호만) — 저장 안 한 편집 행은 막는다(PR #699 리뷰).
       * 확인창을 취소하면 throw — 뷰가 선택을 그대로 둔다(토스트 없음).
       */
      etcOutConfirmButton: async (ctx): Promise<string> => {
        guardOrThrow()
        // async 라 화면 검증의 throw 가 거부(reject)로 돌아간다 — 동기 throw 면 뷰의 onClick 에서 잡히지 않는다.
        syncEdits()
        const targets = pickSelected(ctx)
        if (!targets.length) throw markShown(invalidError(MESSAGES.noSelectedRows))
        if (targets.length > ETC_OUTBOUND_MAX_CONFIRM_SIZE) throw markShown(invalidError(MESSAGES.confirmSizeExceeded))

        // 빈 값이 BE `TransportType.fromCode(null)` 에서 택배로 조용히 떨어져 잘못된 택배사로 운송장이 채번된다 — 되돌릴 수 없다(LOGISPRD-11888).
        const invalidTransportType = findInvalidTransportTypeRow(targets)
        if (invalidTransportType) {
          throw markShown(
            rowInvalidError(invalidTransportType.value ? MESSAGES.transportTypeInvalid(invalidTransportType) : MESSAGES.transportTypeMissing(invalidTransportType)),
          )
        }
        if (findInvalidPhoneRow(targets)) throw markShown(invalidError(MESSAGES.phoneInvalid))
        if (findInvalidZipCodeRow(targets)) throw markShown(invalidError(MESSAGES.zipCodeInvalid))
        const unsaved = targets.find((row) => row.EDITTED)
        if (unsaved) throw markShown(rowInvalidError(MESSAGES.confirmUnsavedRow({ orderNumber: unsaved.ORD_NO })))

        return new Promise<string>((resolve, reject) => {
          PboConfirm.show(MESSAGES.confirmSend, {
            onConfirm: async () => {
              try {
                const res = await confirm(toConfirmBody(targets, resolveShipperCode(targets)))
                // 실패는 200 + result:false 로 온다(1.0 계약) — BE 문구로 알리고 실패로 끝낸다(선택 유지).
                if (!res.result) throw markShown(failureError(res.message ?? MESSAGES.confirmFailed))
                // 재조회는 유지한다 — 부분 확정이면 남은 미확정 행이 보여야 한다. 전량 확정의 0건 안내만 끈다(옛 GI35 는 확정 후 표를 비웠다).
                await refreshCurrentSearch({ quietEmpty: true })
                // 목록 갱신이 끝난 뒤에 띄운다 — 모달은 확정 후처리를 기다리게 하지 않는다.
                notifyUnallocated(res, targets.length)
                resolve(res.message ?? MESSAGES.confirmDone)
              } catch (error) {
                const body = (error as { response?: { data?: { errorCode?: string; message?: string; failedBarcodes?: string[] } } })?.response?.data
                if (body?.errorCode === ERROR_OUTBOUND_SKU_UNMAPPED) {
                  setSkuBlock({ title: MESSAGES.confirmFailedTitle, message: body.message ?? '', barcodes: body.failedBarcodes ?? [] })
                } else if (!isAlreadyShown(error)) {
                  notice.failure(extractMessage(error, MESSAGES.confirmFailed))
                }
                reject(markShown(asError(error)))
              }
            },
            // 취소·닫기 — 확정하지 않았다. 이미 끝난 promise 면 무시된다.
            onClose: () => {
              reject(markShown(new Error(MESSAGES.confirmCancelled)))
            },
          })
        })
      },
      /**
       * 엑셀 다운로드 모달 [확인] — BE 가 마지막 조회 조건(`searchBody`)으로 다시 뽑아 비밀번호 zip 으로 내려준다(LOGISPRD-15797·15805).
       * 생성본 모달에는 [확인] 잠금이 없어 사유코드·상세사유(5글자 이상)·비밀번호를 여기서 막는다(throw = 모달 유지). 비밀번호 규칙은 BE `PasswordValidator` 미러(utils/downloadPassword).
       * 감사로그(사유)는 BE `@AccessAudit` 가 남긴다 — FE 감사 API 를 또 부르면 두 번 남는다.
       * 실패 안내는 useExcelDownload 가 띄우고 resolve 한다 — 성공·실패를 못 가르므로 성공 토스트 없이 모달을 닫는다(RGR05 와 같음).
       */
      etcOutDownloadModal_ok: async (ctx): Promise<undefined> => {
        // 모달이 열린 뒤 재조회가 시작됐을 수 있다 — 받는 중인 조건으로 파일을 뽑지 않는다.
        guardOrThrow()
        const reason = text(ctx.form?.reason)
        const detail = text(ctx.form?.detail).trim()
        const password = text(ctx.form?.password)
        if (!reason || detail.length < REASON_DETAIL_MIN_LENGTH) {
          throw invalidError(MESSAGES.downloadReasonInvalid)
        }
        if (!isValidDownloadPassword(password)) throw invalidError(DOWNLOAD_PASSWORD_INVALID_MESSAGE)
        // 조회 첫 구간과 같은 본문을 문자열로 싣는다 — BE 가 조회 파싱을 그대로 쓴다. page·pageSize 는 BE 가 무시하고 상한까지 뽑는다.
        const params = JSON.stringify({ ...searchBody, type: ETC_OUTBOUND_SEARCH_TYPE.first, page: 1, pageSize: ETC_OUTBOUND_FETCH_SIZE })
        await onDownloadExcel(ETC_OUTBOUND_DOWNLOAD_URL, { params, reasonCode: reason, detailedReason: detail, password })
        return undefined
      },
      /**
       * 엑셀 업로드 모달 [업로드] — 서버 업로드 API 는 1.0 에도 없다. 파일을 열어 표를 신규 행으로 **교체**하고, 사용자가 검토한 뒤 [저장]으로 보낸다.
       * 1.0 공용 툴바가 `setRowData(result)` 로 통째로 갈아 끼웠다(ToolBar.js:296-297) — 얹으면 같은 파일을 두 번 올렸을 때 신규 행이 두 벌 쌓여 둘 다 저장에 실린다.
       * ⚠ 1.0 은 교체 직후 `selectAll()` 로 전량을 골라 뒀는데, 표 선택은 뷰 소유라 여기서 고를 수 없다(컨덕터 후속: 업로드 뒤 선택 슬롯).
       *   지금은 올린 사람이 [저장] 전에 전체 선택을 한 번 눌러야 한다.
       */
      etcOutUploadModal_ok: async (ctx): Promise<string> => {
        const file = ctx.form?.file
        if (!(file instanceof File)) {
          throw invalidError(MESSAGES.uploadFileRequired)
        }
        // 파서(xlsx)는 .xlsx 만 읽는다 — 다른 파일은 영어 파서 오류가 알럿에 그대로 뜬다. 드래거 accept 는 힌트일 뿐이라 여기서 막는다.
        if (!file.name.toLowerCase().endsWith('.xlsx')) {
          throw invalidError(MESSAGES.uploadXlsxOnly)
        }
        // 받는 중에 올리면 받던 구간이 끝난 뒤 저장·재조회가 섞인다 — 다 받은 뒤에 올리게 한다.
        guardOrThrow()
        try {
          const parsed = await parseEtcOutboundExcel(file)
          if (!parsed.length) throw markShown(invalidError(MESSAGES.uploadNoRows))

          const uploaded = parsed.map((row, index) => ({
            ...(row as EtcOutboundEditableRow),
            key: `upload-${index}`,
            EDITTED: 'I' as const,
          }))
          editsRef.current.clear()
          setImportedRows(uploaded)
          callToAddImportLog({ menuCode: EXPORT_LOG_MENU_CD, apiName: `${EXPORT_LOG_MENU_CD}_LIST_IMPORT`, rowCount: uploaded.length })
          // 한 페이지(10,000)를 넘으면 저장·확정이 현재 페이지 선택분만 나간다고 알린다.
          noticeIfUploadExceedsPage(uploaded.length)
          // 목록 검색이 걸려 있으면 방금 넣은 행이 걸러져 보이지 않는다 — 뷰는 업로드 성공 뒤 검색칸·표 필터를 스스로 비운다(생성본 onOk, pbo-design #575).
          lastViewRef.current = { ...lastViewRef.current, keyword: '' }
          return MESSAGES.uploadDone(parsed.length)
        } catch (error) {
          if (isAlreadyShown(error)) throw error
          throw markShown(failureError(error instanceof Error ? error.message : MESSAGES.uploadReadFailed))
        }
      },
    },
    options: {
      // 화주유형·배송유형은 BE 열거형이다(services consts 주석) — 데모 목록의 긴급(퀵)·수출·택배-특송·기타는 2.0 에 없어 고르면 엉뚱한 결과가 난다.
      // 잠금 계정은 고를 수 있는 화주의 유형만 — 대개 자기 유형 하나(LOGISPRD-14949, LOGISPRD-15609).
      ETCOUTSHIPPER_TYPE_OPTIONS: () => (scope.locked ? lockedServiceTypeOptions : LOGISTICS_SERVICE_TYPE_OPTIONS),
      // 화주명 — `POST /api/v1/get-shippers` 전건(코드·명·유형). 데모의 화주명 목데이터(무신사스탠다드 등)는 코드가 아니라 조회에 쓸 수 없다.
      ETCOUTSHIPPER_NAME_OPTIONS: (_demo, filters) => shipperNameOptionsFor(filters),
      ETCOUTTRSTYPE_OPTIONS: () => TRANSPORT_TYPE_OPTIONS,
      // 편집 셀 — 화주코드는 같은 화주 목록, 배송유형은 BE enum, 센터명은 콤보(WH_NM, 'ALL' 제외), 인터페이스 구분은 Y/N.
      ROWS_SPR_NM: () => shipperOptions,
      ROWS_TRS_TYPE: () => TRANSPORT_TYPE_OPTIONS,
      ROWS_WH_NM: () => warehouseNameOptions,
      ROWS_IF_REQ: () => IF_REQ_OPTIONS,
      // 사유코드 — 콤보 DOWNLOAD_CD(LOGISPRD-12385).
      REASONRADIO_OPTIONS: () => downloadReasonOptions,
    },
    // 조회 기본값 — 화주명 = 기본 화주(세션 화주, 옛 GI35Container 의 SPR_NM: userInfo.company — 전환 반영, LOGISPRD-14423·14651. 재고통합 대상은 MUSINSA, LOGISPRD-15718). 화주유형은 '전체'.
    //  기본 화주가 기타출고 지원 화주가 아니면 비워 둔다 — 채우면 [조회] 가 화주유형 불일치로 막힌다.
    //  잠금 계정은 화주유형도 기본 화주의 유형(목록이 아직 안 왔으면 비워 두고 filterFallback 이 채운다). 미지원 화주는 화주명과 같이 비워 둔다.
    filterDefaults: {
      etcOutFilterCard: (demo) => ({
        ...demo,
        ...(defaultShipperCode && ETC_OUTBOUND_AVAILABLE_SHIPPERS.includes(defaultShipperCode) ? { 'etcOutShipper.names': [defaultShipperCode] } : {}),
        ...(lockedDefaultServiceType ? { 'etcOutShipper.types': [lockedDefaultServiceType] } : {}),
      }),
    },
    // 진입 초기값(URL/세션 복원 조건) — 기본값 위에 얹는다. [초기화] 는 기본값으로 돌아간다.
    filterInitial: { etcOutFilterCard: (defaults) => ({ ...defaults, ...restoredFilterValues(restored.params) }) },
    /** 잠금 계정의 화주유형은 화주 목록 응답 뒤에야 정해진다 — 비어 있을 때만 채운다(값이 있는 키는 덮지 않는다). */
    filterFallback: {
      etcOutFilterCard: (current) =>
        lockedDefaultServiceType && asList(current['etcOutShipper.types']).length === 0 ? { 'etcOutShipper.types': [lockedDefaultServiceType] } : undefined,
    },
    /** 화주 고정 계정은 화주유형·화주명 칸을 잠근다(1.0 FilterBar 의 disabled, LOGISPRD-14949). 보이는 것만 잠그므로 값은 기본값·fallback·search 가 따로 고정한다 */
    //  고를 화주가 둘 이상이면 연다(LOGISPRD-15609).
    filterDisabled: canSelectShipper(scope) ? undefined : { service_type: true, shipper_name: true },
    useBusy: () => isBusy,
    // 표를 새로 깔면(discardLocal) 선택을 비운다 — 슬롯 밖 재조회(중복확인 [진행]·진행률 닫힘)도 옛 행 키의 선택이 남지 않게.
    selResetToken,
    opens: {
      // 조회해 받은 행으로 판정한다 — 목록 검색어로 화면이 0건이어도 BE 는 조회 조건 전체를 내려준다.
      etcOutExcelButton: () => {
        if (!guardAction()) return false
        if (!fetchedRows.length) {
          notice.invalid(MESSAGES.excelNoRows)
          return false
        }
        // 사유코드 콤보(DOWNLOAD_CD)가 비면 생성본 모달에는 라디오가 하나도 안 그려져 사용자가 왜 못 고르는지 모른다(옛 모달은 라디오 자리에 안내, LOGISPRD-12615).
        //  모달 안 빈 상태 문구는 컨덕터 후속 — 그때까지 열기 전에 알린다(erpInbound 와 같은 방식).
        if (!downloadReasonOptions.length) {
          notice.failure(DOWNLOAD_REASON_EMPTY_MESSAGE)
          return false
        }
        return true
      },
    },
    /** [초기화] 전 — 저장하지 않은 행이 있으면 물어본다. 취소하면 조회영역 값·행을 그대로 둔다. */
    canReset: () => confirmDiscard(MESSAGES.resetUnsaved, '초기화'),
    // 페이저 크기는 뷰 기본(10,000 — 1.0 공통 페이지 크기, LOGISPRD-15268 에서 GI35 의 5,000 을 통일).
    stateInitial: { pager: { pager: { page: restored.page } }, listq: { 'table.q': restored.keyword } },
    /** 페이지 이동·목록 검색어 확정 — 옛 handlePageChange / handleKeywordChange. */
    onState: (s) => {
      // 받는 중에는 저장을 미룬다 — 덜 온 행으로 뷰가 마지막 페이지로 줄여 알린 값이 복원 페이지를 덮는다(LOGISPRD-15349).
      //  받는 중 사용자가 옮긴 페이지·검색어도 여기로 오므로 버리지 않고 마지막 통지를 두었다가 받기가 끝나면 처리한다.
      if (list.isFirstLoading || list.isFetchingRest) {
        // 표는 지금 다시 그려진다 — 입력분은 바로 넣어 둔다(저장만 미룬다).
        syncEdits()
        pendingViewRef.current = s
        return
      }
      pendingViewRef.current = null
      applyViewState(s)
    },
    /**
     * [초기화] — 필터뿐 아니라 목록도 비운다(1.0 `FilterBar.clearData` 동치). 조건을 지웠는데 결과가 남아 있으면 그 목록이 무슨 조건의 결과인지 알 수 없다.
     * 조회는 트리거하지 않는다 — 화주가 빈 조건이 되면서 쿼리가 비활성이 된다(enabled). 조회영역 값·페이저·선택·검색어는 뷰가 되돌린다.
     */
    onReset: () => {
      awaitRestoreTypeRef.current = false
      setSearchParams(EMPTY_PARAMS)
      discardLocal()
      lastViewRef.current = { page: 1, keyword: '' }
      pendingViewRef.current = null
      // 저장된 조건도 비운다 — 다음 진입이 기본값으로 열리게 한다. `reload()` 를 빼면 탭 스트립이 쓰는 React state 가 옛 검색어를 그대로 들고 있어
      // 다른 탭을 다녀오면 지운 조건이 URL 에 실려 되살아난다.
      MenuStorage.menuOnSession.setSearch({})
      menuSession.reload()
      replaceQuery({})
    },
  }

  return (
    // 한국어 로케일 — 생성본 ConfigProvider 는 테마만 줘 날짜 칸 등 antd 문구가 영어였다(PboKoLocale 주석).
    <PboKoLocale>
      <EtcOutboundPageView wiring={wiring} />
      {/* dev 전용 오류·진행 모달 — 뷰의 ConfigProvider 밖이라 같은 pbo 테마를 다시 건다(gi31 #787 봇 리뷰: 사유 모달과 외형이 달라짐). */}
      <core.ConfigProvider theme={pboLogisticsTheme}>
        <DuplicateOrderModal
          open={duplicate !== null}
          duplicateCount={duplicate?.duplicateCount ?? 0}
          cancelCount={duplicate?.cancelCount ?? 0}
          items={duplicate?.items ?? []}
          onProceed={handleDuplicateProceed}
          onCancel={() => setDuplicate(null)}
        />
        <SkuUnmappedModal
          open={skuBlock !== null}
          title={skuBlock?.title ?? ''}
          message={skuBlock?.message ?? ''}
          barcodes={skuBlock?.barcodes ?? []}
          onClose={() => setSkuBlock(null)}
        />
        <OutboundProgressModal open={progressKey !== null} progress={shownProgress} onClose={() => { handleProgressClose().catch(() => undefined) }} />
      </core.ConfigProvider>
    </PboKoLocale>
  )
}

/**
 * 재고통합 FF 첫 조회를 기다린 뒤 화면을 연다 — 화주 범위·URL 복원값은 마운트 첫 렌더에 한 번 만들어지므로
 * FF 값이 정해지기 전에 만들면 잠긴 29CM·INOOINT 의 MUSINSA 복원이 자기 화주로 덮인다(LOGISPRD-15609).
 */
function OutboundEtc() {
  const { ready, coMingle } = useCoMingleShipperScope()
  if (!ready) return <Loader />
  return <OutboundEtcScreen coMingle={coMingle} />
}

export default OutboundEtc
